import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import PresentationTable from '../../PresentationTable'
import { useListDocuments, useListDocumentsGroupedByTags } from '../../../../api/documentVault'
import { defaultViewSettings } from '../constants'
import { useVaultContext } from '../VaultContextProvider'
import { useStyles } from './styles'
import VaultEmptyState from './VaultEmptyState'

const VaultList = ({
  baseQueryParams,
  labels,
  onClick,
  columns,
  canUpload,
  onLoaded = noop,
  hideIfEmpty = false,
  settings: _settings
}) => {
  const classes = useStyles()
  const [loadVersion, setLoadVersion] = useState(0)
  const { includeRecentGroup, currentTab, viewSettings } = useVaultContext()
  const disableTags = useMemo(() => viewSettings.list.disableTags, [viewSettings])

  const recentsLabel = useMemo(() => (
    labels?.collections?.[currentTab]?.recent || 'Recent'
  ), [labels, currentTab])

  const queryParams = useMemo(() => ({
    ...baseQueryParams,
    includeRecentGroup
  }), [includeRecentGroup, baseQueryParams])

  const settings = {
    ...defaultViewSettings.list,
    ..._settings
  }

  const listQuery = useListDocuments({
    ...queryParams,
    limit: viewSettings.list.limit ?? 100
  }, {
    mapper: data => data.documents.map(doc => ({
      ...doc,
      name: doc.document.name
    })),
    onSuccess: (data) => {
      setLoadVersion(v => v + 1)
      onLoaded?.(data?.documents ?? [])
    },
    onError: (data) => {
      setLoadVersion(v => v + 1)
      onLoaded?.([])
    },
    refetchOnMount: true,
    enabled: disableTags
  })
  const groupedQuery = useListDocumentsGroupedByTags(queryParams, {
    mapper: data => (data.tags ?? []).map(tag => ({
      ...tag,
      name: tag.name ?? (tag.id === 'recent' ? recentsLabel : tag.id),
      _next: true,
      subRows: tag.documents.map((d, i) => ({ ...d, name: d.document.name, _next: null, className: i % 2 === 0 ? 'even' : 'odd' }))
    })),
    onSuccess: (data) => {
      setLoadVersion(v => v + 1)
      onLoaded?.(data?.documents ?? [])
    },
    onError: (data) => {
      setLoadVersion(v => v + 1)
      onLoaded?.([])
    },
    refetchOnMount: true,
    enabled: !disableTags
  })

  const { data, isFetching: isLoading } = disableTags ? listQuery : groupedQuery

  const isEmpty = useMemo(() => !isLoading && !data?.flatMap(t => t.documents)?.length, [isLoading, data])

  if (hideIfEmpty && (isLoading || isEmpty)) return null

  return (
    <>
      <div className={classes.tableContainer}>
        {!isLoading && isEmpty ? (
          <VaultEmptyState
            icon={labels.emptyState.icon}
            title={labels.emptyState.title}
            subtitle={labels.emptyState.subtitle}
            uploadStatement={canUpload ? labels.emptyState.uploadStatement : null}
          />
        ) : (
          <PresentationTable.Wrapper>
            <PresentationTable
              key={loadVersion}
              className={classes.vaultListTable}
              data={data ?? []}
              sortable
              loading={isLoading}
              columns={columns}
              expandable={!disableTags}
              defaultExpanded={settings.defaultExpanded}
              onRowClick={(row, ...rest) => {
                onClick(row.original.document)
              }}
            />
          </PresentationTable.Wrapper>
        )}
      </div>
    </>
  )
}

VaultList.propTypes = {
  baseQueryParams: PropTypes.object,
  labels: PropTypes.object,
  onClick: PropTypes.func,
  columns: PropTypes.array,
  canUpload: PropTypes.bool,
  hideIfEmpty: PropTypes.bool,
  onLoaded: PropTypes.func,
  settings: PropTypes.objectOf({
    defaultExpanded: PropTypes.bool
  })
}

export default VaultList
